import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { SAPerformance, SAPerformanceRowType } from "interfaces/Mueller.Business.Models";
import { getSAPerformance } from "requests/schedulingTimeCard";
import { MCard, MTable } from "components/Shared";
import { dateTimeToStringWithDOW } from "components/Shared/Formatters";
import { ColumnDefinition } from "components/Shared/MTable";
import { TooltipContext, TooltipProviderValue } from "components/Shared/TooltipProvider";
import { filterOnFilterDate } from "components/Homeowner/History/DataFormatters";

interface Props {
  internetUserId: number,
  payPeriodId: number,
  filterDate?: Date,
  className?: string,
}

const SAPerformanceLog = ({ internetUserId, payPeriodId, filterDate, className }: Props) => {
  const mounted = useRef(true);
  const [performance, setPerformance] = useState<SAPerformance[]>(null);
  const tooltipContext = useContext<TooltipProviderValue>(TooltipContext);

  useEffect(() => () => { mounted.current = false }, []);
  
  useEffect(() => {
    setPerformance(null);
    getSAPerformance(internetUserId, payPeriodId, setPerformance, mounted);
  }, [internetUserId, payPeriodId]);

  let colDefs: ColumnDefinition<SAPerformance>[] = [
    { name: "Date/Time", style: { width: "180px" }, dataSource: row => dateTimeToStringWithDOW(row.date) },
    { name: "Control #", style: { width: "90px" }, dataSource: "controlIdNumberForDisplay" },
    { name: "Customer #", style: { width: "190px" }, dataSource: "customerNameNumber" },
    { name: "Survey Type", style: { width: "350px" }, dataSource: "surveyType" },
    { name: "Work Details", className: "performance-details", dataSource: row => <div>{row.details}</div> }, // tables are really stupid so i need to put this in a div and then set a height on the div because you apparently cant force row height on tables?
    { name: "Expected Mins", style: { width: "120px" }, dataSource: "expectedMinutes" },
  ];

  colDefs.forEach(cd => {
    // need to do this wack stuff to get the table to scale right
    if (cd.style?.width) cd.cellStyle = { maxWidth: cd.style.width };
  });

  const onMouseEvent = (eventType: "over" | "out", contents: ReactNode, row: SAPerformance, colDef: ColumnDefinition<SAPerformance>) => {
    if (eventType === "over") {
      // cant go off of data source any more for class name since i needed to insert a div to hack the stupid details column to not stretch height
      if (colDef.className === "performance-details" && row.details) {
        tooltipContext.show(row.details.split("\n").map(d => <>{d}<br /></>), null, "performance-details-tooltip");
      }
        
      if (colDef.dataSource === "customerNameNumber" && contents) {
        tooltipContext.show(contents);
      }
    } else if (eventType === "out") {
      tooltipContext.hide();
    }
  };

  return (
    <MCard className={`sa-performance-log-control${className ? ` ${className}` : ""}`} title="Call Center Performance Log" isFetching={!performance}>
      <MTable<SAPerformance>
        data={performance?.filter(p => filterOnFilterDate(filterDate, p.date))}
        emptyTableText="No Call Center Performance Log Data Available"
        columnDefinitions={colDefs}
        onCellMouseOver={(contents, row, colDef) => onMouseEvent("over", contents, row, colDef)}
        onCellMouseOut={(contents, row, colDef) => onMouseEvent("out", contents, row, colDef)}
        getColSpanData={(row) => {
          if (row.rowType === SAPerformanceRowType.ClockIn || row.rowType === SAPerformanceRowType.ClockOut) {
            return {
              children: row.details,
              className: "clock-in-out-colspan",
            };
          } else {
            return null;
          }
        }}
      />
    </MCard>
  );
};

export default SAPerformanceLog;
